/**
 * Name matching utilities for import anomaly resolution.
 * Uses Levenshtein distance and normalized string comparison
 * to find potential matches between file entries and RNIC entries.
 */

/**
 * Remove accents from a string
 */
export const removeAccents = (str: string): string => {
  return str.normalize('NFD').replaceAll(/[\u0300-\u036f]/g, '');
};

/**
 * Normalize a string for comparison:
 * - Convert to lowercase
 * - Remove accents
 * - Remove extra whitespace
 * - Remove common prefixes (SYNDICAT, COPROPRIETE, etc.)
 */
export const normalizeString = (str: string): string => {
  if (!str) {return '';}
  
  let normalized = str.toLowerCase();
  normalized = removeAccents(normalized);
  normalized = normalized.replaceAll(/\s+/g, ' ').trim();
  
  // Remove common prefixes that don't add matching value
  const prefixesToRemove = [
    'syndicat des coproprietaires',
    'syndicat de coproprietaires',
    'syndicat copropriete',
    'syndicat coproprietaires',
    'copropriete',
    'residence',
    'immeuble',
    'sdc',
  ];
  
  for (const prefix of prefixesToRemove) {
    if (normalized.startsWith(prefix)) {
      normalized = normalized.slice(prefix.length).trim();
    }
  }
  
  return normalized;
};

/**
 * Calculate the Levenshtein distance between two strings.
 * This measures the minimum number of single-character edits
 * (insertions, deletions, substitutions) required to transform one string into another.
 */
export const levenshteinDistance = (str1: string, str2: string): number => {
  const m = str1.length;
  const n = str2.length;
  
  // Create a matrix of distances
  const dp: number[][] = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
  
  // Initialize first column
  for (let i = 0; i <= m; i++) {
    dp[i][0] = i;
  }
  
  // Initialize first row
  for (let j = 0; j <= n; j++) {
    dp[0][j] = j;
  }
  
  // Fill in the rest of the matrix
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(
          dp[i - 1][j],     // deletion
          dp[i][j - 1],     // insertion
          dp[i - 1][j - 1], // substitution
        );
      }
    }
  }
  
  return dp[m][n];
};

/**
 * Calculate similarity score between two strings (0 to 1).
 * Uses normalized Levenshtein distance.
 */
export const calculateSimilarity = (str1: string, str2: string): number => {
  const normalized1 = normalizeString(str1);
  const normalized2 = normalizeString(str2);
  
  if (!normalized1 || !normalized2) {return 0;}
  if (normalized1 === normalized2) {return 1;}
  
  const distance = levenshteinDistance(normalized1, normalized2);
  const maxLength = Math.max(normalized1.length, normalized2.length);
  
  if (maxLength === 0) {return 1;}
  
  return 1 - (distance / maxLength);
};

/**
 * Calculate a confidence score (percentage) for a match.
 */
export const calculateConfidenceScore = (similarity: number): number => {
  return Math.round(similarity * 100);
};

export interface MatchCandidate {
  immatriculation: string;
  name: string;
  rnicAddress?: string;
  rowData?: unknown;
  reason?: string;
}

/**
 * Generate a unique key from immatriculation and name.
 * This handles cases where immatriculation might be empty or duplicated.
 */
export const generateEntryKey = (immatriculation: string, name: string): string => {
  const immatPart = immatriculation?.trim() || '';
  const namePart = name?.trim() || '';
  return `${immatPart}::${namePart}`;
};

export interface MatchResult {
  fileEntry: MatchCandidate;
  rnicEntry: MatchCandidate;
  similarity: number;
  confidenceScore: number;
}

/**
 * Find all potential matches between file entries (not in RNIC)
 * and RNIC entries (not in file).
 * Returns matches sorted by confidence score (highest first).
 */
export const findAllMatches = (
  fileEntries: MatchCandidate[],
  rnicEntries: MatchCandidate[],
  threshold = 0.5,
): MatchResult[] => {
  const matches: MatchResult[] = [];
  const usedRnicIndices = new Set<number>();
  
  // Sort file entries by name length (longer names first for better matching)
  const sortedFileEntries = [...fileEntries].sort((a, b) => b.name.length - a.name.length);
  
  for (const fileEntry of sortedFileEntries) {
    let bestMatch: MatchResult | null = null;
    let bestSimilarity = threshold;
    let bestRnicIndex = -1;
    
    for (let i = 0; i < rnicEntries.length; i++) {
      if (usedRnicIndices.has(i)) {continue;}
      
      const rnicEntry = rnicEntries[i];
      const similarity = calculateSimilarity(fileEntry.name, rnicEntry.name);
      
      if (similarity > bestSimilarity) {
        bestSimilarity = similarity;
        bestRnicIndex = i;
        bestMatch = {
          fileEntry,
          rnicEntry,
          similarity,
          confidenceScore: calculateConfidenceScore(similarity),
        };
      }
    }
    
    if (bestMatch && bestRnicIndex >= 0) {
      matches.push(bestMatch);
      usedRnicIndices.add(bestRnicIndex);
    }
  }
  
  // Sort by confidence score (highest first)
  return matches.sort((a, b) => b.confidenceScore - a.confidenceScore);
};

/**
 * Get entries from fileEntries that weren't matched.
 * Uses composite key (immatriculation + name) to handle entries with empty/duplicate immatriculation.
 */
export const getUnmatchedFileEntries = (
  fileEntries: MatchCandidate[],
  matches: MatchResult[],
): MatchCandidate[] => {
  const matchedKeys = new Set(
    matches.map((m) => generateEntryKey(m.fileEntry.immatriculation, m.fileEntry.name)),
  );
  return fileEntries.filter(
    (entry) => !matchedKeys.has(generateEntryKey(entry.immatriculation, entry.name)),
  );
};

/**
 * Get entries from rnicEntries that weren't matched.
 * Uses composite key (immatriculation + name) to handle entries with empty/duplicate immatriculation.
 */
export const getUnmatchedRnicEntries = (
  rnicEntries: MatchCandidate[],
  matches: MatchResult[],
): MatchCandidate[] => {
  const matchedKeys = new Set(
    matches.map((m) => generateEntryKey(m.rnicEntry.immatriculation, m.rnicEntry.name)),
  );
  return rnicEntries.filter(
    (entry) => !matchedKeys.has(generateEntryKey(entry.immatriculation, entry.name)),
  );
};

